// Popup d'invitation à l'inscription - Affiché quand un visiteur non-inscrit veut utiliser le Cockpit
// Utilise createPortal pour rendre la modale directement dans le body, 
// contournant les problèmes de overflow-hidden des parents
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Shield, Clock, Sparkles, CheckCircle, Camera, CreditCard, Lock, Mail } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function RegisterPromptModal({ isOpen, onClose, actionLabel = "cette fonctionnalité", showGoogleOption = false }) {
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  // Inscription directe avec Google OAuth
  const handleGoogleSignup = async () => {
    setLoadingGoogle(true);
    try {
      // Rediriger vers l'authentification Google pour inscription
      const res = await fetch(`${API_URL}/api/auth/google/auth-url?signup=true`);
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        // Fallback : rediriger vers la page d'inscription
        window.location.href = '/reseau/inscription';
      }
    } catch (error) {
      console.error('Google signup error:', error);
      window.location.href = '/reseau/inscription';
    }
  };

  if (!isOpen) return null;

  // Utiliser createPortal pour rendre la modale hors du contexte parent (contourne overflow-hidden)
  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header avec gradient */}
          <div className="bg-gradient-to-br from-[#1a2a4a] to-[#2d4a6f] p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#E8650A]/20 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="relative">
              <div className="w-16 h-16 bg-[#E8650A] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                <UserPlus size={28} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-white">Rejoins la communauté !</h2>
              <p className="text-white/90 text-sm">
                Pour utiliser {actionLabel}, crée ton compte gratuitement.
              </p>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-6">
            {/* Avantages */}
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={18} className="text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">100% Gratuit</p>
                  <p className="text-sm text-gray-500">Aucun frais d'inscription, jamais.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">2 minutes chrono</p>
                  <p className="text-sm text-gray-500">Inscription rapide et simple.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield size={18} className="text-[#E8650A]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Communauté de confiance</p>
                  <p className="text-sm text-gray-500">1 personne = 1 compte vérifié. Zéro faux profil.</p>
                </div>
              </div>
            </div>

            {/* Process de vérification */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Vérification en 2 étapes</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#1a2a4a] rounded-full flex items-center justify-center">
                    <Camera size={14} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-700">Selfie</span>
                </div>
                <div className="flex-1 border-t border-dashed border-gray-300" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#1a2a4a] rounded-full flex items-center justify-center">
                    <CreditCard size={14} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-700">Pièce d'identité</span>
                </div>
              </div>
            </div>
            
            {/* Message RGPD sécurité */}
            <div className="bg-emerald-50 rounded-xl p-3 mb-6 border border-emerald-100">
              <div className="flex items-center gap-2 text-emerald-700">
                <Lock size={14} className="flex-shrink-0" />
                <p className="text-xs leading-relaxed">
                  <strong>Tes données sont protégées</strong> — Aucun document n'est conservé après vérification. Conforme RGPD.
                </p>
              </div>
            </div>

            {/* Boutons */}
            <div className="space-y-3">
              {/* Bouton Google - Affiché si showGoogleOption est true */}
              {showGoogleOption && (
                <Button 
                  onClick={handleGoogleSignup}
                  disabled={loadingGoogle}
                  className="w-full h-12 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-xl text-base border-2 border-gray-200 flex items-center justify-center gap-3"
                >
                  {loadingGoogle ? (
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                  )}
                  Continuer avec Google
                </Button>
              )}
              
              <Link to="/reseau/inscription" className="block">
                <Button 
                  className="w-full h-12 bg-[#E8650A] hover:bg-[#d45a09] text-white font-semibold rounded-xl text-base"
                  onClick={onClose}
                >
                  {showGoogleOption ? (
                    <>
                      <Mail size={18} className="mr-2" />
                      S'inscrire avec email
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} className="mr-2" />
                      Créer mon compte gratuit
                    </>
                  )}
                </Button>
              </Link>
              
              <Link to="/reseau/connexion" className="block">
                <Button 
                  variant="outline"
                  className="w-full h-11 border-gray-200 text-gray-700 font-medium rounded-xl"
                  onClick={onClose}
                >
                  J'ai déjà un compte
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  // Rendre via portal dans document.body pour éviter les problèmes de stacking context
  return createPortal(modalContent, document.body);
}
